/**
 * Frontend TypeScript type definitions
 * Mirrors backend types but for client-side use
 */

export enum ProductCategory {
  HorseRacing = 'horse-racing',
  GreyhoundRacing = 'greyhound-racing',
  Lottery = 'lottery',
  CardGames = 'card-games',
  Roulette = 'roulette',
  MultiGame = 'multi-game',
}

export enum QualityTier {
  Basic = 'basic',
  Premium = 'premium',
}

/**
 * Supported currencies
 * GBP is the default/base currency for all conversions
 */
export enum Currency {
  GBP = 'GBP', // Default
  EUR = 'EUR',
  USD = 'USD',
  CAD = 'CAD',
  AUD = 'AUD',
}

export interface Price {
  amount: number;
  currency: Currency;
}

/**
 * Product category values
 */
export type ProductCategoryValue = 'horse-racing' | 'greyhound-racing' | 'lottery' | 'card-games' | 'roulette' | 'multi-game';

/**
 * Quality tier values
 */
export type QualityTierValue = 'basic' | 'premium';

export interface Product {
  id: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  category: ProductCategoryValue | string;
  qualityTier: QualityTierValue | string;
  price: Price;
  documentUrl?: string;
  downloadLimit?: number;
  previewUrl?: string;
  imageUrl?: string;
  published: boolean;
  createdAt: any;
  updatedAt: any;
  publishedAt?: any;
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string[];
}

export interface CartItem {
  productId: string;
  product?: Product;
  title?: string;
  quantity: number;
  price: Price;
  total?: Price;
}

export enum OrderStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Refunded = 'refunded',
  Failed = 'failed',
}

export enum PaymentProvider {
  Stripe = 'stripe',
  PayPal = 'paypal',
  Braintree = 'braintree',
}

export interface Order {
  id: string;
  userId: string;
  userEmail: string;
  items: CartItem[];
  subtotal: Price;
  total: Price;
  status: OrderStatus;
  paymentProvider: PaymentProvider | string;
  paymentIntentId: string;
  downloadLocked: boolean;
  createdAt: any;
  updatedAt: any;
  completedAt?: any;
  refundedAt?: any;
}

export interface User {
  uid: string;
  email: string;
  displayName?: string;
  permissions: string[];
}

export interface Category {
  id: ProductCategory;
  name: string;
  description: string;
  icon: string;
}

/**
 * Permission string type
 * Format: "resource:action" or "resource:*"
 *
 * Actions:
 * - create, read, update, delete, publish, etc.: Specific operations
 * - admin: Full administrative dashboard access for the resource category
 * - *: Wildcard for all actions (includes admin)
 *
 * Examples:
 * - "product:read": View products (customer-level)
 * - "product:create": Create new products
 * - "product:admin": Access product management dashboard
 * - "product:*": All product permissions including admin
 *
 * Admin Permissions:
 * The :admin action grants access to the admin panel for that resource category.
 * Users with any :admin permission (product:admin, user:admin, etc.) can access
 * the admin dashboard, where they'll see tabs for categories they have admin access to.
 */
export type Permission = string;

/**
 * Role with permissions
 */
export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
  createdAt: any;
  updatedAt: any;
}

/**
 * Paginated API response
 * Generic type for paginated list endpoints
 */
export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Help Center Category
 */
export interface HelpCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  order: number;
  articleCount?: number;
  published?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Help Article Summary (for listings)
 */
export interface HelpArticleSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  categoryId: string;
  featured: boolean;
  viewCount: number;
  publishedAt?: string;
}

/**
 * Full Help Article (for article pages)
 */
export interface HelpArticle extends HelpArticleSummary {
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string[];
}

/**
 * Help Quick Action Card
 */
export interface HelpQuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  buttonText: string;
  linkUrl: string;
  order: number;
  published?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Contact Form Submission
 */
export type ContactSubject = 'general' | 'support' | 'billing' | 'feedback' | 'other';
export type ContactStatus = 'new' | 'read' | 'responded' | 'closed';

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: ContactSubject;
  message: string;
  status: ContactStatus;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
  respondedAt?: string;
}
